import {FormattedMessage, defineMessages, MessageDescriptor} from 'react-intl';
import {
  chat as chatIcon,
  call as callIcon,
  email as emailIcon,
} from '@sail/icons/react/Icon';

import {
  Audience,
  EligibleChannelsEndpointResponseChannel,
} from '@stripe-internal/conversations-shared-dependencies';
import {ANY_SUPPORTED_LOCALE} from '../../helpers/localeUtils';
import {Topic} from '../TopicSelector';
import {ChannelType} from './Channel';

type ChannelMessagesKeys =
  | 'chatWithSupport'
  | 'requestACall'
  | 'sendAnEmail'
  | 'alternativeLocaleTitle'
  | 'liveWaitTimeEstimateHours'
  | 'liveWaitTimeEstimateMinutes'
  | 'liveWaitTimeEstimateUnderOneMinute'
  | 'liveUnavailable'
  | 'topicUnavailable'
  | 'waitTimeOverflowing';

export const messages: Record<ChannelMessagesKeys, MessageDescriptor> =
  defineMessages({
    chatWithSupport: {
      id: 'support_conversations_client.copilot.link.chatWithSupport',
      description: 'A link that will open a support chat window',
      defaultMessage: 'Chat with us',
    },
    requestACall: {
      id: 'support_conversations_client.copilot.link.requestACall',
      description: 'A link that will open a request a call form window',
      defaultMessage: 'Have us call you',
    },
    sendAnEmail: {
      id: 'support_conversations_client.copilot.link.sendAnEmail',
      description:
        'A link that will open a way for a user to send a support email',
      defaultMessage: 'Send us an email',
    },
    alternativeLocaleTitle: {
      id: 'support_conversations_client.channelSelector.alternativeLocaleTitle',
      description:
        'Title for channel when channel is offered in an alternative locale',
      defaultMessage: '{title} ({language})',
    },
    liveWaitTimeEstimateMinutes: {
      id: 'support_conversations_client.channelSelector.live.waitTimeEstimateMinutes',
      description: 'Wait time estimate in minutes for phone or chat',
      defaultMessage: '{prefix}{minutes} minute wait',
    },
    liveWaitTimeEstimateHours: {
      id: 'support_conversations_client.channelSelector.live.waitTimeEstimateHours',
      description: 'Wait time estimate in hours for phone or chat',
      defaultMessage: '{prefix}{hours} hour wait',
    },
    liveWaitTimeEstimateUnderOneMinute: {
      id: 'support_conversations_client.channelSelector.live.waitTimeEstimateUnderOneMinute',
      description: 'Wait time estimate when chat wait is under one minute',
      defaultMessage: '<1m',
    },
    liveUnavailable: {
      id: 'support_conversations_client.channelSelector.live.unavailable',
      description: 'Message if a live channel is unavailable',
      defaultMessage: 'Currently unavailable',
    },
    waitTimeOverflowing: {
      id: 'support_conversations_client.channelSelector.waitTimeOverflowing',
      description: 'Unable to predict wait time because of high volume.',
      defaultMessage: 'Longer than usual',
    },
    topicUnavailable: {
      id: 'support_conversations_client.channelSelector.live.topicUnavailable',
      description:
        'Message if the selected topic is unavailable in the support channel',
      defaultMessage: 'Unavailable for selected topic',
    },
  });

const formatWaitTime = (
  waitTime: number,
): {hours: number; minutes: number; prefix: string} => {
  const hours = Math.floor(waitTime / 60);
  const shouldPrefixWithGreaterThan = hours > 0 && waitTime % 60 !== 0;
  return {
    minutes: waitTime % 60,
    hours,
    prefix: shouldPrefixWithGreaterThan ? '>' : '~',
  };
};

export const getWaitTime = (
  channel: EligibleChannelsEndpointResponseChannel,
  ineligibleTopic: boolean,
  audience?: Audience,
) => {
  if (!channel.operational) {
    return <FormattedMessage {...messages.liveUnavailable} />;
  } else if (ineligibleTopic) {
    return <FormattedMessage {...messages.topicUnavailable} />;
  } else if (channel.overflowing) {
    return <FormattedMessage {...messages.waitTimeOverflowing} />;
  } else if ('waitTime' in channel && typeof channel.waitTime === 'number') {
    if (
      audience === 'link' &&
      channel.type === 'chat' &&
      channel.waitTime === 1
    ) {
      return (
        <FormattedMessage {...messages.liveWaitTimeEstimateUnderOneMinute} />
      );
    }

    const {hours, minutes, prefix} = formatWaitTime(channel.waitTime);
    if (hours) {
      return (
        <FormattedMessage
          {...messages.liveWaitTimeEstimateHours}
          values={{hours, prefix}}
        />
      );
    }
    return (
      <FormattedMessage
        {...messages.liveWaitTimeEstimateMinutes}
        values={{minutes, prefix}}
      />
    );
  }
  return null;
};

const getTitleForChannel = (
  channel: EligibleChannelsEndpointResponseChannel,
  currentLocale: string,
) => {
  let title = <FormattedMessage {...messages.sendAnEmail} />;
  if (channel.type === 'chat') {
    title = <FormattedMessage {...messages.chatWithSupport} />;
  } else if (channel.type === 'request_a_call') {
    title = <FormattedMessage {...messages.requestACall} />;
  }

  if (
    'language' in channel &&
    'locale' in channel &&
    channel.locale !== currentLocale
  ) {
    return (
      <FormattedMessage
        {...messages.alternativeLocaleTitle}
        values={{
          title,
          language: channel.language,
        }}
      />
    );
  }

  return title;
};

export const getDisplayChannels = (props: {
  channels: Array<EligibleChannelsEndpointResponseChannel> | null | undefined;
  selectedTopic: Topic | null | undefined;
  disableTopicSelector?: boolean;
  locale: string;
  audience?: Audience;
  showUnavailableChannels: boolean;
}): Array<ChannelType> => {
  const channels = props.channels || [];
  const displayChannels: Array<ChannelType> = [];

  channels.forEach((channel) => {
    const ineligibleTopic = !!(
      props.selectedTopic &&
      channel.ineligibleTopics.includes(props.selectedTopic)
    );

    const disabled =
      !channel.operational ||
      (!props.selectedTopic && !props.disableTopicSelector) ||
      ineligibleTopic;

    if (!props.showUnavailableChannels && disabled) {
      return;
    }

    if (channel.type === 'chat') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        disabled,
        waitEstimate: getWaitTime(channel, ineligibleTopic, props.audience),
        icon: chatIcon,
        locale: channel.locale,
        type: channel.type,
      });
    } else if (channel.type === 'request_a_call') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        disabled,
        waitEstimate: getWaitTime(channel, ineligibleTopic, props.audience),
        icon: callIcon,
        locale: channel.locale,
        type: channel.type,
      });
    } else if (channel.type === 'email') {
      displayChannels.push({
        title: getTitleForChannel(channel, props.locale),
        waitEstimate: getWaitTime(channel, ineligibleTopic, props.audience),
        disabled,
        icon: emailIcon,
        type: 'email',
        locale: ANY_SUPPORTED_LOCALE,
      });
    }
  });

  return displayChannels;
};
